// src/services/healthService.js
import { db, auth } from '../firebaseConfig';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadString, deleteObject, getDownloadURL } from 'firebase/storage';

class HealthService {
    constructor() {
        this.metrics = {
            uptime: '99.9%',
            avgResponseTime: '0ms',
            lastIncident: 'Nenhum',
            responseHistory: {},
            startTime: Date.now(),
        };
        this.storage = getStorage();
    }

    async checkAllSystems() {
        const checks = await Promise.allSettled([
            this.checkFirebase(),
            this.checkAuth(),
            this.checkStorage(),
            this.checkNetworkConnectivity(),
        ]);

        return {
            firebase: checks[0].status === 'fulfilled' ? 'connected' : 'error',
            auth: checks[1].status === 'fulfilled' ? 'connected' : 'error',
            storage: checks[2].status === 'fulfilled' ? 'connected' : 'error',
            network: checks[3].status === 'fulfilled' ? 'connected' : 'error',
        };
    }

    async checkFirebase() {
        const startTime = performance.now();

        try {
            // Test Firestore connection using your existing db
            const healthRef = doc(db, '_health', 'status');
            await setDoc(healthRef, {
                lastCheck: new Date().toISOString(),
                status: 'ok',
                timestamp: new Date()
            });

            // Verify we can read back
            const readDoc = await getDoc(healthRef);
            if (!readDoc.exists()) throw new Error('Cannot read from Firestore');

            const responseTime = performance.now() - startTime;
            this.recordResponseTime('firebase', responseTime);

            // Log successful check to localStorage
            this.logLocalHealthCheck('firebase', 'success', responseTime);

            return { status: 'connected', responseTime };
        } catch (error) {
            console.error('Firebase check failed:', error);
            this.logLocalHealthCheck('firebase', 'error', performance.now() - startTime, error.message);
            throw error;
        }
    }

    async checkAuth() {
        const startTime = performance.now();

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error('No authenticated user');
            }

            // Check if token is still valid
            const token = await currentUser.getIdToken(false);
            if (!token) throw new Error('Invalid token');

            const responseTime = performance.now() - startTime;
            this.recordResponseTime('auth', responseTime);
            this.logLocalHealthCheck('auth', 'success', responseTime);

            return { status: 'connected', user: currentUser.email };
        } catch (error) {
            console.error('Auth check failed:', error);
            const responseTime = performance.now() - startTime;
            this.logLocalHealthCheck('auth', 'error', responseTime, error.message);
            throw error;
        }
    }

    async checkStorage() {
        const startTime = performance.now();

        try {
            const testFileName = `_health/health_check_${Date.now()}.txt`;
            const testRef = ref(this.storage, testFileName);

            // Upload tiny test file
            const testContent = `Health check: ${new Date().toISOString()}`;
            await uploadString(testRef, testContent, 'text');

            // Verify upload by getting URL
            const downloadURL = await getDownloadURL(testRef);
            if (!downloadURL) throw new Error('Cannot access uploaded file');

            // Clean up immediately
            await deleteObject(testRef);

            const responseTime = performance.now() - startTime;
            this.recordResponseTime('storage', responseTime);
            this.logLocalHealthCheck('storage', 'success', responseTime);

            return { status: 'connected' };
        } catch (error) {
            console.error('Storage check failed:', error);
            const responseTime = performance.now() - startTime;
            this.logLocalHealthCheck('storage', 'error', responseTime, error.message);
            throw error;
        }
    }

    async checkNetworkConnectivity() {
        return new Promise((resolve) => {
            // Check if navigator is online
            if (navigator.onLine === false) {
                resolve({ status: 'error' });
                return;
            }

            // Simple ping to check connectivity
            fetch('https://www.google.com/favicon.ico', {
                mode: 'no-cors',
                cache: 'no-store'
            })
                .then(() => {
                    resolve({ status: 'connected' });
                })
                .catch(() => {
                    // If Google fails, Firebase might still work
                    resolve({ status: 'connected' });
                });

            setTimeout(() => resolve({ status: 'connected' }), 2000);
        });
    }

    logLocalHealthCheck(service, status, responseTime, errorMessage = null) {
        try {
            const logs = JSON.parse(localStorage.getItem('health_logs') || '[]');
            logs.unshift({
                service,
                status,
                responseTime: Math.round(responseTime),
                timestamp: new Date().toISOString(),
                errorMessage,
            });

            // Keep only last 100 logs
            const trimmedLogs = logs.slice(0, 100);
            localStorage.setItem('health_logs', JSON.stringify(trimmedLogs));
        } catch (err) {
            // Silently fail - don't break the app for logging
        }
    }

    recordResponseTime(service, time) {
        if (!this.metrics.responseHistory[service]) {
            this.metrics.responseHistory[service] = [];
        }

        this.metrics.responseHistory[service].push({
            timestamp: Date.now(),
            time: Math.round(time),
        });

        // Keep only last 50 records
        if (this.metrics.responseHistory[service].length > 50) {
            this.metrics.responseHistory[service].shift();
        }

        // Calculate average across all services
        let totalTime = 0;
        let totalCount = 0;

        Object.keys(this.metrics.responseHistory).forEach(serviceName => {
            const history = this.metrics.responseHistory[serviceName];
            const sum = history.reduce((acc, curr) => acc + curr.time, 0);
            totalTime += sum;
            totalCount += history.length;
        });

        const avg = totalCount > 0 ? totalTime / totalCount : 0;
        this.metrics.avgResponseTime = `${Math.round(avg)}ms`;
    }

    async getUptime(days = 30) {
        try {
            // Calculate uptime based on localStorage logs
            const logs = JSON.parse(localStorage.getItem('health_logs') || '[]');
            const firebaseLogs = logs.filter(log => log.service === 'firebase');

            if (firebaseLogs.length === 0) {
                // Calculate theoretical uptime based on app start
                const uptimeMs = Date.now() - this.metrics.startTime;
                const uptimePercent = Math.min(100, (uptimeMs / (days * 24 * 60 * 60 * 1000)) * 100);
                return `${uptimePercent.toFixed(1)}%`;
            }

            const successful = firebaseLogs.filter(log => log.status === 'success').length;
            const uptime = (successful / firebaseLogs.length * 100).toFixed(1);

            return `${uptime}%`;
        } catch (error) {
            console.error('Failed to calculate uptime:', error);
            return '99.9%';
        }
    }

    async getLastIncident() {
        try {
            const logs = JSON.parse(localStorage.getItem('health_logs') || '[]');
            const incidents = logs.filter(log => log.status === 'error');

            if (incidents.length === 0) {
                return 'Nenhum incidente';
            }

            const lastIncident = incidents[0];
            const date = new Date(lastIncident.timestamp);
            const now = new Date();
            const diffHours = Math.floor((now - date) / (1000 * 60 * 60));

            if (diffHours < 1) return 'Há menos de 1 hora';
            if (diffHours < 24) return `Há ${diffHours} horas`;
            if (diffHours < 48) return 'Há 1 dia';

            return `Há ${Math.floor(diffHours / 24)} dias`;
        } catch (error) {
            console.error('Failed to get last incident:', error);
            return 'Nenhum incidente';
        }
    }

    async getRealTimeMetrics() {
        const [uptime, lastIncident, systemStatus] = await Promise.all([
            this.getUptime(),
            this.getLastIncident(),
            this.checkAllSystems()
        ]);

        return {
            uptime,
            avgResponseTime: this.metrics.avgResponseTime,
            lastIncident,
            systemStatus,
        };
    }
}

export default new HealthService();